import { LightweightElement } from "@/store/lightweight-elements-slice";
import { ITreeRenderDecision } from "@/utils/tree-generation/tree-render-filters";
import { convertToDateString } from "@faro-lotv/foundation";
import {
  GUID,
  IElement,
  IElementType,
  IElementTypeHint,
} from "@faro-lotv/ielement-types";

/**
 * @returns Shows a flat list of all Locations with timestamps as their children specifically for the report and snapshot tree
 * @param iElement IElement to use as base for the filtering
 * @param parentType Parent type of the given IElement
 * @param videoModeCopyTagId id of the tag identifying rooms copied for video mode datasets
 * @param customDate Custom date to use instead of the element's createdAt date
 */
export function locationFilterForReportAndSnapshotTree(
  iElement: IElement | LightweightElement,
  parentType?: string,
  videoModeCopyTagId?: GUID,
  customDate?: string,
): ITreeRenderDecision {
  const date = customDate ?? iElement.createdAt;
  if (iElement.type === IElementType.section) {
    if (parentType === IElementType.timeSeries) {
      const videoModeCopy =
        !!videoModeCopyTagId &&
        !!iElement.labelIds?.some((label) => label === videoModeCopyTagId);

      const displayName = convertToDateString(date);

      return {
        // Stop recursive rendering of tree at this level
        shouldKeepChildren: true,

        shouldKeepElement: !videoModeCopy,

        displayName,
      };
    }

    return {
      displayName: undefined,
      shouldKeepElement: true,
      shouldKeepChildren: true,
    };
  }

  if (
    iElement.type === IElementType.timeSeries &&
    iElement.typeHint === IElementTypeHint.room
  ) {
    return {
      shouldKeepChildren: true,
      shouldKeepElement: false,
    };
  }

  // Parent of all the 360 Images
  if (
    iElement.type === IElementType.group &&
    iElement.typeHint === IElementTypeHint.rooms &&
    iElement.childrenIds?.length
  ) {
    return {
      shouldKeepElement: false,
      shouldKeepChildren: true,
      displayName: "360° Photos",
    };
  }

  if (
    iElement.type === IElementType.group &&
    iElement.typeHint === IElementTypeHint.attachments
  ) {
    return {
      shouldKeepChildren: true,
      shouldKeepElement: false,
    };
  }

  if (iElement.type === IElementType.img2d) {
    return {
      shouldKeepElement: true,
      shouldKeepChildren: false,
      displayName: convertToDateString(date),
    };
  }

  if (
    iElement.type === IElementType.group &&
    iElement.typeHint === IElementTypeHint.nodes
  ) {
    return {
      shouldKeepChildren: false,
      shouldKeepElement: false,
    };
  }

  // Ignore other elements, but keep their children
  return {
    shouldKeepElement: false,
    shouldKeepChildren: true,
  };
}
